/**
 * PUC EDM - NemoOptimisation — reference hot-water network captured from the Nemo
 * integration environment (eid 300145213). Values are real; person names are anonymised.
 * Use these fixtures in prototype `mocks.ts` files to start from realistic data.
 */
import type { Size2MixNetworkSummary } from '../types';

export const SIZE2MIX_USER = 'Analyste Nemo';

export const PUC_EDM_NETWORK: Size2MixNetworkSummary & {
    description: string;
    timezone: string;
    status: 'Actif' | 'Inactif';
    studyPhase: string;
    contract: string;
    owner: string;
    readers: string[];
    editors: string[];
    useExternalOptimisationService: boolean;
    createdAt: string;
    updatedAt: string;
} = {
    eid: '300145213',
    name: 'PUC EDM - NemoOptimisation',
    fluidType: 'hot_water',
    description: '-',
    timezone: 'UTC',
    status: 'Actif',
    studyPhase: 'Autre',
    contract: '00 - Onboarding nemo-opti eau chaude',
    owner: SIZE2MIX_USER,
    readers: [],
    editors: [],
    useExternalOptimisationService: true,
    createdAt: '10-07-2026 à 14:56:37',
    updatedAt: '01-09-2026 à 19:05:47',
};

// ── Topologie ───────────────────────────────────────────────────────────────
export const PUC_EDM_TOPOLOGY_PARAMETERS = {
    temperature: { networkMax: 200, injectionMax: 200, injectionMin: 60, networkMin: 10, ambient: 10 },
    pressure: { max: 17, min: 2, singularLossRate: 0 },
    substation: { deltaPNominal: 0, deltaTNominal: null as number | null },
    outdoorTemperature: { max: 18, inter: null as number | null, min: -5 },
    sizing: { maxVelocity: null as number | null, maxPressureGradient: null as number | null },
    pipeCatalog: {
        name: 'Axiom-Acier PN25_Isolation 3',
        rows: [
            [32, 37.2, 0.13], [40, 43.1, 0.14], [50, 54.5, 0.16], [65, 70.3, 0.17], [80, 82.5, 0.18], [100, 107.1, 0.19],
            [125, 132.5, 0.22], [150, 160.3, 0.24], [200, 210.1, 0.25], [250, 263, 0.25], [300, 312.7, 0.28], [350, 344.4, 0.27],
            [400, 393.8, 0.31], [450, 444.4, 0.35], [500, 495.4, 0.27], [600, 595.8, 0.32],
        ].map(([dn, innerDiameter, thermalCoef]) => ({ dn, innerDiameter, roughness: 0.1, thermalCoef })),
    },
};

export interface NetworkDeparture {
    site: string;
    departure: string;
    maxMassFlow: number | null;
    tAtTextMin: number | null;
    tAtTextInter: number | null;
    tAtTextMax: number | null;
    pumpGroup: string;
}
export const PUC_EDM_DEPARTURES: NetworkDeparture[] = [
    { site: 'Chaufferie gaz', departure: 'Gaz', maxMassFlow: null, tAtTextMin: 109, tAtTextInter: 109, tAtTextMax: 80, pumpGroup: 'Pompes Sud' },
    { site: 'Chaufferie Biomasse', departure: 'Biomasse', maxMassFlow: null, tAtTextMin: 100, tAtTextInter: null, tAtTextMax: 80, pumpGroup: 'Collector Chaufferie Biomasse pumps group' },
    { site: 'UVE', departure: 'UVE', maxMassFlow: null, tAtTextMin: 100, tAtTextInter: null, tAtTextMax: 80, pumpGroup: 'Collector UVE pumps group' },
    { site: 'Recuperation', departure: 'Recup', maxMassFlow: null, tAtTextMin: 100, tAtTextInter: null, tAtTextMax: null, pumpGroup: 'Collector Recuperation pumps group' },
];

// ── Production ──────────────────────────────────────────────────────────────
export interface ProductionUnit {
    site: string;
    name: string;
    kind: 'boiler' | 'heat-pump' | 'exchanger';
    priority: number | null;
    fuelContract: string | null;
    defaultPrice: string;
    pMin: number;
    pMax: number;
    efficiencyAtPMin: number;
    efficiencyAtPMax: number;
    rampUp: number | null;
    rampDown: number | null;
}
export const PUC_EDM_PRODUCTION_UNITS: ProductionUnit[] = [
    { site: 'Chaufferie Biomasse', name: 'Biomasse 2', kind: 'boiler', priority: null, fuelContract: 'Contrat_Biomasse', defaultPrice: '30', pMin: 0.1, pMax: 5, efficiencyAtPMin: 1, efficiencyAtPMax: 1, rampUp: null, rampDown: null },
    { site: 'Chaufferie Biomasse', name: 'Chaudière Biomasse', kind: 'boiler', priority: 3, fuelContract: 'Contrat_Biomasse', defaultPrice: '30', pMin: 5, pMax: 15, efficiencyAtPMin: 1, efficiencyAtPMax: 1, rampUp: null, rampDown: null },
    { site: 'Chaufferie gaz', name: 'Chaudière gaz', kind: 'boiler', priority: 4, fuelContract: 'Contrat gaz', defaultPrice: '100', pMin: 0, pMax: 10, efficiencyAtPMin: 1, efficiencyAtPMax: 1, rampUp: null, rampDown: null },
    { site: 'Chaufferie gaz', name: 'Chaudière gaz 2', kind: 'boiler', priority: 4, fuelContract: 'Contrat gaz', defaultPrice: '100', pMin: 0, pMax: 20, efficiencyAtPMin: 1, efficiencyAtPMax: 1, rampUp: null, rampDown: null },
    { site: 'Chaufferie gaz', name: 'Chaudière gaz 3', kind: 'boiler', priority: 4, fuelContract: 'Contrat gaz', defaultPrice: '100', pMin: 0, pMax: 20, efficiencyAtPMin: 1, efficiencyAtPMax: 1, rampUp: null, rampDown: null },
    { site: 'UVE', name: 'Echangeur UVE', kind: 'exchanger', priority: 1, fuelContract: 'Contrat_UVE', defaultPrice: '10', pMin: 0, pMax: 8, efficiencyAtPMin: 1, efficiencyAtPMax: 1, rampUp: null, rampDown: null },
    { site: 'Recuperation', name: 'PAC Recuperation', kind: 'heat-pump', priority: 2, fuelContract: null, defaultPrice: '1, 50', pMin: 1, pMax: 3, efficiencyAtPMin: 1, efficiencyAtPMax: 1, rampUp: null, rampDown: null },
];

export interface ProductionSite {
    name: string;
    departure: string;
    units: string[];
    pumps: string[];
    pumpGroup: string;
}
export const PUC_EDM_SITES: ProductionSite[] = [
    { name: 'Chaufferie Biomasse', departure: 'Biomasse', units: ['Biomasse 2', 'Chaudière Biomasse'], pumps: ['Chaufferie Biomasse_Default'], pumpGroup: 'Collector Chaufferie Biomasse pumps group' },
    { name: 'Chaufferie gaz', departure: 'Gaz', units: ['Chaudière gaz', 'Chaudière gaz 2', 'Chaudière gaz 3'], pumps: ['Pompes existantes'], pumpGroup: 'Pompes Sud' },
    { name: 'Recuperation', departure: 'Recup', units: ['PAC Recuperation'], pumps: ['Pompes_Recuperation'], pumpGroup: 'Collector Recuperation pumps group' },
    { name: 'UVE', departure: 'UVE', units: ['Echangeur UVE'], pumps: ['UVE_Default'], pumpGroup: 'Collector UVE pumps group' },
];

export interface FuelContract {
    name: string;
    linkedUnits: number;
    fixedPrice: number | null;
    variablePrice: number | null;
    calorificValue: number;
    description: string;
    /** Contribution to each renewable-energy rate (0-1). */
    renewableShare: Record<string, number>;
    co2: number | null;
}
export const PUC_EDM_FUEL_CONTRACTS: FuelContract[] = [
    { name: 'Chaleur_industrielle', linkedUnits: 1, fixedPrice: null, variablePrice: 1, calorificValue: 3_600_000_000, description: '-', renewableShare: { Taux_ENR: 0.7, test: 1 }, co2: null },
    { name: 'Contrat gaz', linkedUnits: 3, fixedPrice: null, variablePrice: 100, calorificValue: 3_600_000_000, description: '-', renewableShare: { Taux_ENR: 0, test: 0 }, co2: 1 },
    { name: 'Contrat_Biomasse', linkedUnits: 2, fixedPrice: null, variablePrice: 30, calorificValue: 3_600_000_000, description: '-', renewableShare: { Taux_ENR: 1, test: 1 }, co2: null },
    { name: 'Contrat_UVE', linkedUnits: 1, fixedPrice: null, variablePrice: 10, calorificValue: 3_600_000_000, description: '-', renewableShare: { Taux_ENR: 0.05, test: 1 }, co2: null },
    { name: 'Electricité', linkedUnits: 1, fixedPrice: null, variablePrice: 50, calorificValue: 3_600_000_000, description: '-', renewableShare: { Taux_ENR: 1, test: 0 }, co2: null },
];

export const PUC_EDM_RENEWABLE_THRESHOLDS = [
    { name: 'Taux_ENR', min: 0.7, max: null as number | null },
    { name: 'test', min: 0.8, max: null as number | null },
];

export interface PowerContract {
    name: string;
    linkedSites: number;
    linkedUnits: number;
    linkedPumpGroups: number;
    fixedPrice: number;
    variablePrice: number | null;
    description: string;
}
export const PUC_EDM_POWER_CONTRACTS: PowerContract[] = [
    { name: 'Contrat elec', linkedSites: 4, linkedUnits: 0, linkedPumpGroups: 0, fixedPrice: 0, variablePrice: 0, description: '-' },
];

export interface DistributionPump {
    site: string;
    pumpGroup: string;
    pump: string;
    type: 'Variable' | 'Fixe';
    flow: string;
    deltaP: string;
    powerContract: string;
}
export const PUC_EDM_PUMPS: DistributionPump[] = [
    { site: 'UVE', pumpGroup: 'Collector UVE pumps group', pump: 'UVE_Default', type: 'Variable', flow: '0 0.2605194 257.914206 260.5194', deltaP: '23.5 23.5 1e-07', powerContract: 'Contrat elec' },
    { site: 'Recuperation', pumpGroup: 'Collector Recuperation pumps group', pump: 'Pompes_Recuperation', type: 'Variable', flow: '0 0.15631164 154.7485236 156.31164', deltaP: '23.5 23.5 1e-07', powerContract: 'Contrat elec' },
    { site: 'Chaufferie gaz', pumpGroup: 'Pompes Sud', pump: 'Pompes existantes', type: 'Variable', flow: '0 1 2269.65 2292.57', deltaP: '10 10 1e-07', powerContract: 'Contrat elec' },
    { site: 'Chaufferie Biomasse', pumpGroup: 'Collector Chaufferie Biomasse pumps group', pump: 'Chaufferie Biomasse_Default', type: 'Variable', flow: '0 1.0420776 1031.656824 1042.0776', deltaP: '23.5 23.5 1e-07', powerContract: 'Contrat elec' },
];

// ── Import / export ─────────────────────────────────────────────────────────
export const PUC_EDM_IMPORTS = [
    { id: 'imp-2', status: 'Succès', name: 'import_PUC EDM - NemoOptimisation', type: 'Import NEMO', date: '2026/07/10 14:56' },
    { id: 'imp-1', status: 'Succès', name: 'import_PUC EDM - NemoOptimisation', type: 'Import NEMO', date: '2026/06/08 09:34' },
];
export const PUC_EDM_EXPORTS: typeof PUC_EDM_IMPORTS = [];

// ── Plannings ───────────────────────────────────────────────────────────────
export interface PlanningConstraint {
    id: string;
    target: string;
    kind: 'Indisponible' | 'Minimum' | 'Maximum' | 'Forcé à' | 'Démarrage interdit';
    start: string;
    end: string;
    value?: number;
}
export const PUC_EDM_PRODUCTION_PLANNINGS: Array<{ id: string; name: string; constraints: PlanningConstraint[] }> = [
    { id: 'pp-maint', name: 'Maintenances', constraints: [{ id: 'c1', target: 'Biomasse 2', kind: 'Indisponible', start: '2025-12-31', end: '2026-02-28' }] },
    { id: 'pp-test', name: 'test', constraints: [] },
    { id: 'pp-nostorage', name: 'Sans stockage', constraints: [] },
];
export const PUC_EDM_TOPOLOGY_PLANNINGS: Array<{ id: string; name: string; constraints: PlanningConstraint[] }> = [
    { id: 'tp-cas04', name: 'Cas04 Topo CPR-21778', constraints: [{ id: 't1', target: 'PipeGroup-122184095', kind: 'Indisponible', start: '1900-01-01', end: '2900-01-01' }] },
];
export const TOPOLOGY_PLANNING_ROWS = ['Fermeture canalisation', 'Fermeture groupe de canalisations', 'Fermeture vanne', 'Fermeture groupe de vannes'] as const;
export const PLANNING_LEGEND = ['Indisponible', 'Minimum', 'Maximum', 'Forcé à', 'Forcé au minimum à', 'Forcé au maximum à', 'Démarrage interdit'] as const;

// ── Optimisations ───────────────────────────────────────────────────────────
export type OptimisationType = 'Paramétrique atemporelle' | 'Court terme' | 'Trajectoire';
export interface OptimisationSummary {
    eid: string;
    status: 'Succès' | 'En cours' | 'Échec' | 'En attente';
    type: OptimisationType;
    name: string;
    steps: number;
    attributes: string;
    createdBy: string;
    source: 'CubicWeb' | 'Optimisations';
    date: string;
}
export const PUC_EDM_OPTIMISATIONS: OptimisationSummary[] = [
    { eid: '304254353', status: 'Succès', type: 'Paramétrique atemporelle', name: 'test descr', steps: 2, attributes: '', createdBy: SIZE2MIX_USER, source: 'CubicWeb', date: '2026/07/27 11:30' },
    { eid: '304254300', status: 'Succès', type: 'Paramétrique atemporelle', name: 'test descr', steps: 2, attributes: '', createdBy: SIZE2MIX_USER, source: 'Optimisations', date: '2026/07/27 11:29' },
    { eid: '304252911', status: 'Succès', type: 'Court terme', name: 'Optimisation_304252911', steps: 2, attributes: '', createdBy: SIZE2MIX_USER, source: 'CubicWeb', date: '2026/07/27 11:26' },
    { eid: '304252900', status: 'Succès', type: 'Court terme', name: 'Optimisation 304252911', steps: 2, attributes: '', createdBy: SIZE2MIX_USER, source: 'Optimisations', date: '2026/07/27 11:24' },
];
export const OPTIMISATION_TYPES: OptimisationType[] = ['Court terme', 'Paramétrique atemporelle', 'Trajectoire'];

export const PUC_EDM_OPTIMISATION_PARAMETERS = {
    name: 'Optimisation_304252911',
    start: '01-01-2026 00:00',
    steps: 2,
    detailedSteps: 2,
    horizon: 'Court terme',
    granularity: 'Horaire',
    driving: 'Pilotage par consommation' as 'Pilotage par consommation' | 'Pilotage par production',
    substationRegulation: 'Delta de température' as 'Delta de température' | 'Température de sortie',
    diversityType: 'Consommation' as 'Automatique' | 'Canalisation' | 'Consommation',
    diversityCoefficient: 1,
    automaticPipeSizing: false,
    inputs: [
        { label: 'Consommations', file: 'consumptions.csv' },
        { label: 'Température sèche', file: 'predicted_temperature.csv' },
        { label: "Prix de l'electricité", file: 'elec_price.csv' },
    ],
};

/** Hourly results of optimisation 304252911 (2 steps). Units: MW, € */
export const PUC_EDM_RESULTS = {
    timestamps: ['2026-01-01 00:00', '2026-01-01 01:00'],
    availableProduction: [81, 81],
    directProduction: [40.93, 39.0],
    delivered: [40.6, 38.673],
    thermalLosses: [0.33, 0.327],
    notDelivered: [0, 0],
    productionBySite: {
        Recuperation: [3, 0],
        UVE: [8, 0],
        'Chaufferie Biomasse': [15, 0],
        'Chaufferie gaz': [14.93, 13],
    } as Record<string, number[]>,
    costs: {
        fuel: [2070.5, 1877.24],
        auxiliaryElectricity: [6.575, 6.315],
        pollutants: [14.93, 13.0],
    },
    pressureRange: { nodes: [2, 13.21], pipes: [2.29, 13.11] },
};

export interface OptimisationLog {
    level: 'Critique' | 'Majeur' | 'Modéré' | 'Mineur';
    message: string;
    count: number;
    details: Array<{ object: string; value: string }>;
}
export const PUC_EDM_LOGS: { slacks: Array<{ name: string; value: number }>; errors: OptimisationLog[] } = {
    slacks: [],
    errors: [
        { level: 'Modéré', message: 'All snapshot files should be in input directory. Run has been executed with live nemo data.', count: 1, details: [{ object: 'Run', value: '-' }] },
        {
            level: 'Modéré',
            message: 'Distribution pumps of each thermal water storage or pump group must have the same power contract and the same group of power contracts.',
            count: 4,
            details: PUC_EDM_PUMPS.map((p) => ({ object: p.pumpGroup, value: p.powerContract })),
        },
        {
            level: 'Mineur',
            message: 'Pipes ratio of altitude difference over length should not exceed 20%',
            count: 9,
            details: [
                'GE_D02755F_PI_10161', 'GE_D02755F_PI_11357', 'GE_D02755F_PI_1741', 'GE_D02755F_PI_2863', 'GE_D02755F_PI_3802', 'GE_D02755F_PI_4961', 'GE_D02755F_PI_6062', 'GE_D02755F_PI_7166', 'GE_D02755F_PI_7929',
            ].map((id, i) => ({ object: id, value: `${21 + i * 3}%` })),
        },
    ],
};

// ── App-level lists (home, network list, contracts) ─────────────────────────
export interface NetworkListItem {
    eid: string;
    name: string;
    fluidType: Size2MixNetworkSummary['fluidType'];
    createdAt: string;
    createdBy: string;
    updatedAt: string;
}
export const SAMPLE_NETWORKS: NetworkListItem[] = [
    { eid: '300145213', name: 'PUC EDM - NemoOptimisation', fluidType: 'hot_water', createdAt: '2026/07/10 14:56', createdBy: SIZE2MIX_USER, updatedAt: '01-09-2026 à 19:05:47' },
    { eid: '300100001', name: 'Aix-en-Provence - DEMO', fluidType: 'hot_water', createdAt: '2025/03/12 10:02', createdBy: SIZE2MIX_USER, updatedAt: '09-10-2026 à 11:05:25' },
    { eid: '300100002', name: 'test PUC', fluidType: 'hot_water', createdAt: '2026/05/02 09:10', createdBy: SIZE2MIX_USER, updatedAt: '18-09-2026 à 11:28:28' },
    { eid: '300100003', name: 'PAC Dogger_COP variable + appointe gaz', fluidType: 'hot_water', createdAt: '2026/07/01 16:03', createdBy: SIZE2MIX_USER, updatedAt: '31-08-2026 à 16:03:54' },
    { eid: '300100004', name: 'PAC sans stockage', fluidType: 'hot_water', createdAt: '2026/07/02 20:23', createdBy: SIZE2MIX_USER, updatedAt: '22-07-2026 à 20:23:45' },
    { eid: '300100005', name: 'CENTRE Study v2-Tokyo', fluidType: 'cold_water', createdAt: '2026/04/24 14:41', createdBy: SIZE2MIX_USER, updatedAt: '24-04-2026 à 14:41:48' },
    { eid: '300100006', name: 'BERCY_2025_STUDY', fluidType: 'cold_water', createdAt: '2025/11/27 11:03', createdBy: SIZE2MIX_USER, updatedAt: '27-11-2025 à 11:03:35' },
    { eid: '300100007', name: 'autotest-small-mdb', fluidType: 'steam', createdAt: '2026/10/09 17:28', createdBy: 'Compte technique', updatedAt: '09-10-2026 à 17:28:00' },
    { eid: '300100008', name: 'selenium-ref-hotwater-copy', fluidType: 'hot_water', createdAt: '2026/10/09 01:53', createdBy: 'Compte technique', updatedAt: '09-10-2026 à 01:53:00' },
    { eid: '300100009', name: 'CPR-22656', fluidType: 'cold_water', createdAt: '2026/10/08 14:41', createdBy: 'Compte technique', updatedAt: '08-10-2026 à 14:41:00' },
];
export const TOTAL_NETWORK_COUNT = 8465;

export interface ContractListItem {
    id: string;
    code: string;
    name: string;
    fluidType: Size2MixNetworkSummary['fluidType'];
    studyPhase: string;
    status: 'Actif' | 'Inactif';
    description: string;
    owner: string;
    updatedAt: string;
    networks: string[];
}
export const SAMPLE_CONTRACTS: ContractListItem[] = [
    { id: 'k1', code: '00', name: 'Onboarding nemo-opti eau chaude', fluidType: 'hot_water', studyPhase: 'Autre', status: 'Actif', description: '-', owner: SIZE2MIX_USER, updatedAt: '2026/09/01', networks: ['PUC EDM - NemoOptimisation', 'test PUC'] },
    { id: 'k2', code: '0000C', name: 'Test Bêta', fluidType: 'hot_water', studyPhase: 'Avant-projet', status: 'Actif', description: '-', owner: SIZE2MIX_USER, updatedAt: '2026/08/14', networks: ['Aix-en-Provence - DEMO'] },
    { id: 'k3', code: '0000F', name: 'Test Bêta', fluidType: 'cold_water', studyPhase: 'Avant-projet', status: 'Actif', description: '-', owner: SIZE2MIX_USER, updatedAt: '2026/08/14', networks: ['CENTRE Study v2-Tokyo'] },
    { id: 'k4', code: '08021996', name: 'PAC/Géothermie - Cas de test', fluidType: 'hot_water', studyPhase: 'Autre', status: 'Actif', description: 'Cas de test PAC', owner: SIZE2MIX_USER, updatedAt: '2026/07/22', networks: ['PAC sans stockage', 'PAC Dogger_COP variable + appointe gaz'] },
    { id: 'k5', code: '09021996', name: 'Stockages froid', fluidType: 'cold_water', studyPhase: 'Autre', status: 'Actif', description: '-', owner: SIZE2MIX_USER, updatedAt: '2026/06/30', networks: ['BERCY_2025_STUDY'] },
    { id: 'k6', code: 'CAC-25', name: 'end2end test beaucoup de réseaux', fluidType: 'hot_water', studyPhase: 'Autre', status: 'Inactif', description: 'Contrat de test e2e', owner: 'Compte technique', updatedAt: '2026/10/08', networks: ['selenium-ref-hotwater-copy', 'CPR-22656', 'autotest-small-mdb'] },
];
export const TOTAL_CONTRACT_COUNT = 31;

export const COREAI_SUGGESTIONS = [
    { icon: 'build', text: 'Explique-moi comment lancer une vérification hydraulique de mon réseau' },
    { icon: 'description', text: 'Explique-moi comment lancer un étude de fiabilité' },
    { icon: 'map', text: 'Montre-moi les nouvelles configurations des réseaux' },
    { icon: 'location_city', text: 'Trouve les réseaux dans une ville définie' },
];
